import { useEffect, useMemo, useState } from "react";
import { ChevronDown, ChevronUp, Ticket } from "lucide-react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  buildSupportCaseThread,
  isSupportCaseTerminal,
  SUPPORT_CASE_POLL_MS,
  type SupportFormField,
} from "@gaole/shared";
import { api, ApiError } from "../lib/api";
import { fmtDate, timeAgo } from "../lib/format";
import { toast } from "../stores/toast";
import { useQueryRecordId } from "../lib/use-query-record";
import { Button, SearchSelect, Badge, statusColor, Textarea } from "../components/ui";
import { DataTable, type Column } from "../components/DataTable";
import { ListPageShell } from "../components/shell/ListPageShell";
import { RightSidebarPanel } from "../components/RightSidebarPanel";
import { CaseChatThread } from "../components/support/CaseChatThread";
import { CaseChatComposer } from "../components/support/CaseChatComposer";

const CASE_STATUS_OPTIONS = [
  { value: "OPEN", label: "Open" },
  { value: "IN_PROGRESS", label: "In progress" },
  { value: "RESOLVED", label: "Resolved" },
  { value: "REJECTED", label: "Rejected" },
] as const;

type CaseStatus = "" | "OPEN" | "IN_PROGRESS" | "RESOLVED" | "REJECTED";

interface CaseComment {
  id: string;
  body: string;
  authorId: string | null;
  fileKeys: string[];
  createdAt: string;
}

interface CaseRow {
  id: string;
  caseNo: string;
  status: string;
  kind: string;
  userId: string | null;
  data: Record<string, unknown>;
  createdAt: string;
  user: { displayName: string; email: string } | null;
  form: {
    name: string;
    fields?: SupportFormField[];
    subCategory?: { name: string; parent?: { name: string } | null } | null;
  } | null;
  comments: CaseComment[];
}

interface CaseDetail extends CaseRow {
  comments: CaseComment[];
}

const STATUS_OPTIONS: { value: CaseStatus; label: string }[] = [
  { value: "", label: "All" },
  { value: "OPEN", label: "Open" },
  { value: "IN_PROGRESS", label: "In progress" },
  { value: "RESOLVED", label: "Resolved" },
  { value: "REJECTED", label: "Rejected" },
];

export function Cases() {
  const qc = useQueryClient();
  const [statusFilter, setStatusFilter] = useState<CaseStatus>("");
  const [selected, setSelected] = useState<CaseRow | null>(null);
  const [newStatus, setNewStatus] = useState<string>("OPEN");
  const [statusComment, setStatusComment] = useState("");
  const [actionsOpen, setActionsOpen] = useState(false);
  const [sending, setSending] = useState(false);
  const { recordId, clearRecordId } = useQueryRecordId();

  const { data, isLoading } = useQuery({
    queryKey: ["ai-cases", statusFilter],
    queryFn: () =>
      api<{ items: CaseRow[] }>(`/ai/cases${statusFilter ? `?status=${statusFilter}` : ""}`),
  });

  const selectedId = selected?.id;

  const caseDetailQuery = useQuery({
    queryKey: ["ai-case", selectedId],
    enabled: !!selectedId,
    queryFn: () => api<{ case: CaseDetail }>(`/ai/cases/${selectedId}`),
    refetchInterval: (query) => {
      const status = query.state.data?.case.status;
      if (!status || isSupportCaseTerminal(status)) return false;
      return SUPPORT_CASE_POLL_MS;
    },
  });

  const detail = caseDetailQuery.data?.case ?? selected;

  useEffect(() => {
    if (!recordId || !data?.items) return;
    const row = data.items.find((item) => item.id === recordId);
    if (!row) return;
    setSelected(row);
    setNewStatus(row.status);
    setStatusComment("");
    clearRecordId();
  }, [recordId, data?.items, clearRecordId]);

  const statusMut = useMutation({
    mutationFn: ({ id, status, comment }: { id: string; status: string; comment?: string }) =>
      api(`/ai/cases/${id}/status`, { method: "POST", body: { status, comment } }),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: ["ai-cases"] });
      void qc.invalidateQueries({ queryKey: ["ai-case", vars.id] });
      toast("success", "Case updated");
      setStatusComment("");
    },
    onError: (e) => toast("error", e instanceof ApiError ? e.message : "Update failed"),
  });

  const commentMut = useMutation({
    mutationFn: ({ id, body, fileKeys }: { id: string; body: string; fileKeys: string[] }) =>
      api(`/ai/cases/${id}/comments`, { method: "POST", body: { body, fileKeys } }),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: ["ai-cases"] });
      void qc.invalidateQueries({ queryKey: ["ai-case", vars.id] });
      toast("success", "Reply sent");
    },
    onError: (e) => toast("error", e instanceof ApiError ? e.message : "Reply failed"),
  });

  function openDetail(row: CaseRow) {
    setSelected(row);
    setNewStatus(row.status);
    setStatusComment("");
    setActionsOpen(false);
  }

  const formFields = useMemo(
    () => detail?.form?.fields ?? [],
    [detail?.form?.fields],
  );

  const threadItems = useMemo(() => {
    if (!detail) return [];
    return buildSupportCaseThread(
      {
        id: detail.id,
        userId: detail.userId,
        createdAt: detail.createdAt,
        data: detail.data,
        comments: (detail.comments ?? []).map((c) => ({
          id: c.id,
          authorId: c.authorId,
          body: c.body,
          fileKeys: c.fileKeys ?? [],
          createdAt: c.createdAt,
        })),
      },
      formFields,
    );
  }, [detail, formFields]);

  const columns: Column<CaseRow>[] = [
    {
      key: "caseNo",
      header: "Case#",
      sortable: true,
      sortValue: (r) => r.caseNo,
      render: (r) => <span className="font-mono text-xs">{r.caseNo}</span>,
    },
    {
      key: "form",
      header: "Form",
      sortable: true,
      sortValue: (r) => r.form?.name ?? r.kind,
      render: (r) => r.form?.name ?? r.kind,
    },
    {
      key: "user",
      header: "Customer",
      hideSm: true,
      sortable: true,
      sortValue: (r) => r.user?.displayName ?? r.user?.email ?? "",
      exportValue: (r) => r.user?.email ?? r.user?.displayName ?? "",
      render: (r) => (
        <div>
          <p className="text-xs">{r.user?.displayName ?? "—"}</p>
          <p className="text-[10px] text-white/40">{r.user?.email}</p>
        </div>
      ),
    },
    {
      key: "status",
      header: "Status",
      sortable: true,
      sortValue: (r) => r.status,
      render: (r) => <Badge color={statusColor(r.status)}>{r.status.replace(/_/g, " ")}</Badge>,
    },
    {
      key: "created",
      header: "Created",
      hideSm: true,
      sortable: true,
      sortValue: (r) => r.createdAt,
      exportValue: (r) => fmtDate(r.createdAt),
      render: (r) => <span className="text-xs text-white/50">{timeAgo(r.createdAt)}</span>,
    },
  ];

  const closed = detail ? isSupportCaseTerminal(detail.status) : true;

  async function handleSend(payload: { body: string; fileKeys: string[] }) {
    if (!detail) return;
    setSending(true);
    try {
      await commentMut.mutateAsync({ id: detail.id, ...payload });
    } finally {
      setSending(false);
    }
  }

  return (
    <ListPageShell>
      <DataTable
        columns={columns}
        rows={data?.items}
        rowKey={(r) => r.id}
        loading={isLoading}
        exportFilename="cases"
        toolbar={{
          views: {
            options: STATUS_OPTIONS,
            value: statusFilter,
            onChange: (v) => setStatusFilter(v as CaseStatus),
          },
        }}
        onRowClick={openDetail}
        activeKey={selected?.id}
        empty={{ icon: Ticket, title: "No cases", hint: statusFilter ? "Try another filter." : undefined }}
      />

      <RightSidebarPanel
        open={!!detail}
        wide
        title={detail?.caseNo ?? "Case"}
        subtitle={detail?.form?.name}
        onClose={() => setSelected(null)}
        footer={
          detail ? (
            <CaseChatComposer
              disabled={closed}
              busy={sending || commentMut.isPending}
              onSend={handleSend}
            />
          ) : undefined
        }
      >
        {detail && (
          <div className="-mx-1 flex min-h-[min(520px,calc(100vh-12rem))] flex-col gap-3">
            <div className="flex flex-wrap items-center gap-2">
              <Badge color={statusColor(detail.status)}>{detail.status.replace(/_/g, " ")}</Badge>
              <span className="text-xs text-muted">{fmtDate(detail.createdAt)}</span>
            </div>

            {detail.user && (
              <div className="rounded-xl border border-line bg-hover/50 p-3 text-xs">
                <p className="font-medium text-foreground">{detail.user.displayName}</p>
                <p className="text-muted">{detail.user.email}</p>
              </div>
            )}

            <div className="rounded-xl border border-line">
              <button
                type="button"
                className="flex w-full items-center justify-between px-3 py-2 text-left text-xs font-semibold uppercase tracking-wider text-muted"
                onClick={() => setActionsOpen((v) => !v)}
              >
                Case actions
                {actionsOpen ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
              </button>
              {actionsOpen && (
                <div className="space-y-2 border-t border-line px-3 py-3">
                  <SearchSelect
                    options={[...CASE_STATUS_OPTIONS]}
                    value={newStatus}
                    onChange={setNewStatus}
                  />
                  <Textarea
                    rows={2}
                    placeholder="Optional note to customer…"
                    value={statusComment}
                    onChange={(e) => setStatusComment(e.target.value)}
                  />
                  <Button
                    size="sm"
                    disabled={statusMut.isPending || newStatus === detail.status}
                    onClick={() =>
                      statusMut.mutate({
                        id: detail.id,
                        status: newStatus,
                        comment: statusComment.trim() || undefined,
                      })
                    }
                  >
                    Update status
                  </Button>
                </div>
              )}
            </div>

            <CaseChatThread
              caseId={detail.id}
              items={threadItems}
              viewerSide="staff"
              className="min-h-0 flex-1 pb-2"
            />
          </div>
        )}
      </RightSidebarPanel>
    </ListPageShell>
  );
}
